import {
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import LinkIcon from "@mui/icons-material/Link";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import React from "react";
import { useContentStore } from "../../stores/contentStore";
import { locale } from "../../utils/LocaleManager";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import IosShareOutlinedIcon from "@mui/icons-material/IosShareOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import { useRolePermissionsStore } from "../../stores/rolePermissionsStore";
import jsonToCsvExport from "json-to-csv-export";
import { exportTranscript } from "../../services/contentServices";
import { toast } from "react-toastify";

const ThumbnailMenu = (props: any) => {
  const selectedThumbnails = useContentStore(
    (state: any) => state.selectedThumbnails,
  );
  const videoName = props.name || selectedThumbnails[0]?.name;
  const setIsShowDeleteDialog = useContentStore(
    (state: any) => state.setIsShowDeleteDialog,
  );
  const setIsShowRetagDialog = useContentStore(
    (state: any) => state.setIsShowRetagDialog,
  );
  const rolePermissions = useRolePermissionsStore(
    (state: any) => state.rolePermissions,
  );
  const setIsShowRenameDialog = useContentStore(
    (state: any) => state.setIsShowRenameDialog,
  );
  const setTempVideoTitle = useContentStore(
    (state: any) => state.setTempVideoTitle,
  );
  const setIsShowVerticalAssignmentDialog = useContentStore(
    (state: any) => state.setIsShowVerticalAssignmentDialog,
  );

  const assignToVerticalMenuItem = {
    key: "assign-to-vertical",
    icon: <CategoryOutlinedIcon fontSize="small" />,
    text: locale.control_assign_to_vertical,
    ariaLabel: videoName ? `Assign ${videoName} to vertical` : undefined,
    onClick: () => {
      // FE-CAP-01 STAVC-326: re-check live capability at click time
      // so a revocation between render and click cannot open the dialog.
      if (useRolePermissionsStore.getState().rolePermissions?.video?.retag === true) {
        props?.setOpen(false);
        // STAVC-326 FE-A11Y-01 — the dialog captures
        // document.activeElement on mount; MUI may return focus to the
        // anchor asynchronously after the menu closes, so ensure the
        // anchor has focus synchronously so the dialog can restore to it.
        if (props?.anchorEl && typeof props.anchorEl.focus === 'function') {
          props.anchorEl.focus();
        }

        setIsShowVerticalAssignmentDialog(true);
      }
    },
  };

  const unassignFromVerticalMenuItem = {
    key: "unassign-from-vertical",
    icon: <CategoryOutlinedIcon fontSize="small" />,
    text: locale.control_unassign_from_vertical,
    onClick: () => {
      if (
        useRolePermissionsStore.getState().rolePermissions?.video?.retag ===
        true
      ) {
        props?.setOpen(false);
        props.onUnassignVertical();
      }
    },
  };

  const menuItemsIfMultiple = [
    ...(rolePermissions?.video?.download
      ? [
          {
            key: "download",
            icon: <FileDownloadOutlinedIcon fontSize="small" />,
            text: locale.control_download,
            onClick: () => {
              props?.setOpen(false);
              props.onDownload();
            },
          },
          {
            isDivider: true,
            key: "divider-3",
          },
        ]
      : []),
    ...(rolePermissions?.video?.move && props.onClickMove && !props.isBrowseOnly
      ? [
          {
            key: "move",
            icon: <DriveFileMoveOutlinedIcon fontSize="small" />,
            text: locale.control_move,
            onClick: () => {
              props?.setOpen(false);
              props.onClickMove();
            },
          },
        ]
      : []),
    ...(rolePermissions?.video?.retag && props.onClickRetag && !props.isBrowseOnly
      ? [
          {
            key: "add-tag",
            icon: <AddIcon fontSize="small" />,
            text: locale.add_tag,
            onClick: () => {
              props?.setOpen(false);
              setIsShowRetagDialog(true);
            },
          },
        ]
      : []),
    ...(rolePermissions?.video?.retag ? [assignToVerticalMenuItem] : []),
    ...(rolePermissions?.video?.retag && props.onUnassignVertical
      ? [unassignFromVerticalMenuItem]
      : []),
    ...(rolePermissions?.video?.delete && !props.isBrowseOnly
      ? [
          {
            key: "delete",
            icon: <DeleteOutlineOutlinedIcon fontSize="small" />,
            text: locale.control_delete,
            onClick: () => {
              props?.setOpen(false);
              setIsShowDeleteDialog(true);
            },
          },
        ]
      : []),
  ];

  const hasSingleMutationActions =
    !props.isBrowseOnly &&
    ((rolePermissions?.video?.move && props.onClickMove) ||
      rolePermissions?.video?.retag ||
      rolePermissions?.video?.rename ||
      rolePermissions?.video?.delete);
  const hasSinglePostMutationActions =
    !props.isBrowseOnly &&
    ((rolePermissions?.video?.retag && props.onClickRetag) ||
      rolePermissions?.video?.download);

  const menuItemsIfSingle = [
    ...(rolePermissions?.video?.view === true
      ? [
          {
            key: "play",
            icon: <PlayCircleOutlineIcon fontSize="small" />,
            text: locale.control_play,
            onClick: () => {
              if (useRolePermissionsStore.getState().rolePermissions?.video?.view === true) {
                props?.setOpen(false);
                props?.setSelected(true);
                props.onClickPlay();
              }
            },
          },
          {
            isDivider: true,
            key: "divider-0",
          },
        ]
      : []),
    ...(rolePermissions?.video?.view === true && props.onCopyLinks && !props.isBrowseOnly
      ? [
          {
            key: "copy",
            icon: <LinkIcon fontSize="small" />,
            text: locale.control_copy,
            onClick: () => {
              if (useRolePermissionsStore.getState().rolePermissions?.video?.view === true) {
                props?.setOpen(false);
                props?.setSelected(true);
                props.onCopyLinks();
              }
            },
          },
        ]
      : []),
    ...(rolePermissions?.video?.download
      ? [
          {
            key: "download",
            icon: <FileDownloadOutlinedIcon fontSize="small" />,
            text: locale.control_download,
            onClick: () => {
              props?.setOpen(false);
              props.onDownload();
            },
          },
        ]
      : []),
    ...(rolePermissions?.video?.view === true
      ? [
          {
            key: "details",
            icon: <InfoOutlinedIcon fontSize="small" />,
            text: locale.control_details,
            onClick: () => {
              if (useRolePermissionsStore.getState().rolePermissions?.video?.view === true) {
                props?.setOpen(false);
                props.onClickDetails();
              }
            },
          },
        ]
      : []),
    ...(hasSingleMutationActions
      ? [{ isDivider: true, key: "divider-1" }]
      : []),
    ...(rolePermissions?.video?.move && props.onClickMove && !props.isBrowseOnly
      ? [
          {
            key: "move",
            icon: <DriveFileMoveOutlinedIcon fontSize="small" />,
            text: locale.control_move,
            onClick: () => {
              props?.setOpen(false);
              props.onClickMove();
            },
          },
        ]
      : []),
    //  STAVC-326 VERT-02 single-video assignment; reference shows the
    // entry in the Verticals browse view, so do NOT gate on isBrowseOnly.
    ...(rolePermissions?.video?.retag ? [assignToVerticalMenuItem] : []),
    ...(rolePermissions?.video?.retag && props.onUnassignVertical
      ? [unassignFromVerticalMenuItem]
      : []),
    ...(rolePermissions?.video?.rename && !props.isBrowseOnly
      ? [
          {
            key: "rename-tag",
            icon: <DriveFileRenameOutlineOutlinedIcon fontSize="small" />,
            text: locale.control_rename,
            onClick: () => {
              props?.setOpen(false);
              setTempVideoTitle(selectedThumbnails[0]?.name);
              setIsShowRenameDialog(true);
            },
          },
        ]
      : []),
    ...(rolePermissions?.video?.delete && !props.isBrowseOnly
      ? [
          {
            key: "delete",
            icon: <DeleteOutlineOutlinedIcon fontSize="small" />,
            text: locale.control_delete,
            onClick: () => {
              props?.setOpen(false);
              setIsShowDeleteDialog(true);
            },
          },
        ]
      : []),
    ...(hasSingleMutationActions && hasSinglePostMutationActions
      ? [{ isDivider: true, key: "divider-2" }]
      : []),
    ...(rolePermissions?.video?.retag && props.onClickRetag && !props.isBrowseOnly
      ? [
          {
            key: "add-tag",
            icon: <AddIcon fontSize="small" />,
            text: locale.add_tag,
            onClick: () => {
              props?.setOpen(false);
              setIsShowRetagDialog(true);
            },
          },
        ]
      : []),
    ...(rolePermissions?.video?.download && !props.isBrowseOnly
      ? [
          {
            key: "export-tag",
            icon: <IosShareOutlinedIcon fontSize="small" />,
            text: locale.exportTranscript,
            onClick: () => {
              props?.setOpen(false);
              (async () => {
                const transcripts = await exportTranscript(
                  selectedThumbnails[0].videoIndexId,
                );

                if (!transcripts?.data || transcripts.data.length === 0) {
                  toast.error(
                    locale.transcript_export_error,
                    {
                      position: "bottom-left",
                      autoClose: 2000,
                      hideProgressBar: false,
                      closeOnClick: false,
                      pauseOnHover: false,
                      pauseOnFocusLoss: false,
                      draggable: true,
                      progress: undefined,
                      theme: "colored",
                    },
                  );
                  return;
                }

                jsonToCsvExport({
                  data: transcripts.data,
                  filename: `${selectedThumbnails[0].name}.csv`,
                });
              })();
            },
          },
        ]
      : []),
  ];

  const menuItemsIfManage = [
    ...(rolePermissions?.video?.retag
      ? [
          {
            key: "retag",
            icon: <RefreshIcon fontSize="small" />,
            text: locale.control_retag,
            onClick: () => {
              props?.setOpen(false);
              props?.onRetag();
            },
          },
        ]
      : []),
    ...(rolePermissions?.video?.delete
      ? [
          {
            key: "delete",
            icon: <DeleteOutlineOutlinedIcon fontSize="small" />,
            text: locale.control_delete_video,
            onClick: () => {
              props?.setOpen(false);
              setIsShowDeleteDialog(true);
            },
          },
        ]
      : []),
  ];

  const renderMenuItem = (item: any) => {
    if (item.isDivider) {
      return <Divider key={item.key} />;
    }

    return (
      <MenuItem
        key={item.key}
        onClick={item.onClick}
        disabled={item.disabled ? item.disabled : false}
        aria-label={item.ariaLabel}
      >
        <ListItemIcon>{item.icon}</ListItemIcon>
        <ListItemText>{item.text}</ListItemText>
      </MenuItem>
    );
  };

  const renderThumbnailMenus = () => {
    if (props?.isMultiple) {
      return menuItemsIfMultiple.map((item: any) => renderMenuItem(item));
    }

    return menuItemsIfSingle.map((item: any) => renderMenuItem(item));
  };

  return (
    <Menu
      anchorEl={props?.anchorEl}
      open={props?.open}
      onClose={() => {
        props?.setOpen(false);
      }}
      anchorReference={props?.contextMenu ? "anchorPosition" : "anchorEl"}
      anchorPosition={
        props?.contextMenu !== null
          ? {
            top: props?.contextMenu.mouseY,
            left: props?.contextMenu.mouseX,
          }
          : undefined
      }
      slotProps={{ paper: { sx: { width: 250 } } }}
    >
      {!props.isManage ? (
        renderThumbnailMenus()
      ) : (
        menuItemsIfManage.map((item: any) => renderMenuItem(item))
      )}
    </Menu>
  );
};

export default ThumbnailMenu;
